import { useEffect, useMemo, useState } from "react";
import { ITEM_CONDITIONS } from "@farnsworth/shared";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Select,
  Section,
  Spinner,
  cn,
} from "../components/ui.js";
import { ItemEditor } from "../components/ItemEditor.js";
import { ItemDetailDialog } from "../components/ItemDetailDialog.js";
import { TransferDialog } from "../components/TransferDialog.js";
import { MassEditDialog } from "../components/MassEditDialog.js";
import { BundleDialog } from "../components/BundleDialog.js";
import {
  DEFAULT_FILTERS,
  type ItemFilters,
  toSearchParams,
  useBulkDelete,
  useBulkRestore,
  useCategories,
  useDeleteItem,
  useItemSuggestions,
  useItems,
  useRestoreItem,
} from "../hooks/useInventory.js";
import { api, downloadCsv } from "../lib/api.js";
import { formatCurrency, formatDate } from "../lib/format.js";
import type { Category, Item } from "../types.js";

function flattenCategories(categories: Category[], depth = 0): Array<{ id: string; label: string }> {
  return categories.flatMap((category) => [
    { id: category.id, label: `${"— ".repeat(depth)}${category.name}` },
    ...flattenCategories(category.children, depth + 1),
  ]);
}

function StatusBadges({ item }: { item: Item }) {
  return (
    <div className="flex gap-1">
      {item.sold ? <Badge tone="red">Sold</Badge> : <Badge tone="green">Available</Badge>}
      {item.isWebsiteFeatured ? <Badge tone="indigo">Website</Badge> : null}
    </div>
  );
}

interface GtPoppediaAutofillSummary {
  scanned: number;
  linked: number;
  matched: number;
  filled: number;
  unmatched: number;
  ambiguous: number;
  errors: number;
  results: Array<{ id: string; name: string; status: string; matchedName?: string; fields: string[]; message?: string }>;
}

interface SavedInventoryView {
  id: string;
  name: string;
  filters: ItemFilters;
}

function readSavedViews(): SavedInventoryView[] {
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem("inventory-saved-views") ?? "[]");
    return Array.isArray(value) ? value as SavedInventoryView[] : [];
  } catch {
    return [];
  }
}

export function InventoryPage() {
  const [filters, setFilters] = useState<ItemFilters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  // undefined = editor closed, null = creating, Item = editing
  const [editorTarget, setEditorTarget] = useState<Item | null | undefined>(undefined);
  /** Id of the item whose read-only card is open. */
  const [detailId, setDetailId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Item | null>(null);
  const [transferTab, setTransferTab] = useState<null | "import" | "export">(null);
  const [showMassEdit, setShowMassEdit] = useState(false);
  const [showBundle, setShowBundle] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [autofill, setAutofill] = useState<{ busy: boolean; summary: GtPoppediaAutofillSummary | null }>({ busy: false, summary: null });
  const [view, setView] = useState<"table" | "cards">(() => (window.localStorage.getItem("inventory-view") === "cards" ? "cards" : "table"));
  const [savedViews, setSavedViews] = useState<SavedInventoryView[]>(readSavedViews);
  const [savedViewName, setSavedViewName] = useState("");
  const [selectedSavedView, setSelectedSavedView] = useState("");

  const itemsQuery = useItems(filters);
  const categoriesQuery = useCategories();
  const suggestionsQuery = useItemSuggestions();
  const deleteItem = useDeleteItem();
  const bulkDelete = useBulkDelete();
  const restoreItem = useRestoreItem();
  const bulkRestore = useBulkRestore();
  const recycleBin = filters.status === "deleted";

  const search = toSearchParams(filters);

  // A new result set invalidates the current selection.
  useEffect(() => {
    setSelected([]);
  }, [search]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setFilters((previous) =>
        previous.q === searchInput ? previous : { ...previous, q: searchInput, offset: 0 },
      );
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const categoryOptions = useMemo(
    () => flattenCategories(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );

  function changeView(next: "table" | "cards") {
    setView(next);
    window.localStorage.setItem("inventory-view", next);
  }

  const filtersActive = Boolean(filters.q || filters.categoryId || filters.condition || filters.series || filters.location || filters.forSale || filters.featured || filters.needsValuation || filters.status !== "available" || filters.sort !== "updatedAt" || filters.order !== "desc");

  function resetFilters() {
    setSearchInput("");
    setFilters(DEFAULT_FILTERS);
    setSelectedSavedView("");
  }

  function patchFilters(patch: Partial<ItemFilters>) {
    setFilters((previous) => ({ ...previous, ...patch, offset: patch.offset ?? 0 }));
    setSelectedSavedView("");
  }

  function applyQuickView(name: string) {
    setSearchInput("");
    setSelectedSavedView("");
    const presets: Record<string, Partial<ItemFilters>> = {
      Available: { status: "available" },
      "For sale": { status: "available", forSale: true },
      Featured: { status: "available", featured: true },
      Sold: { status: "sold" },
      "Needs valuation": { status: "available", needsValuation: true },
      "Recently added": { status: "all", sort: "createdAt", order: "desc" },
    };
    setFilters({ ...DEFAULT_FILTERS, ...presets[name], offset: 0 });
  }

  function saveCurrentView() {
    const name = savedViewName.trim();
    if (!name) return;
    const entry = { id: `view-${Date.now()}`, name, filters: { ...filters, q: searchInput, offset: 0 } };
    const next = [...savedViews.filter((view) => view.name.toLowerCase() !== name.toLowerCase()), entry];
    setSavedViews(next);
    window.localStorage.setItem("inventory-saved-views", JSON.stringify(next));
    setSelectedSavedView(entry.id);
    setSavedViewName("");
  }

  function selectSavedView(id: string) {
    setSelectedSavedView(id);
    const saved = savedViews.find((entry) => entry.id === id);
    if (!saved) return;
    setSearchInput(saved.filters.q);
    setFilters({ ...DEFAULT_FILTERS, ...saved.filters, offset: 0 });
  }

  function removeFilter(key: keyof ItemFilters) {
    if (key === "q") setSearchInput("");
    patchFilters({ [key]: DEFAULT_FILTERS[key] } as Partial<ItemFilters>);
  }

  const items = itemsQuery.data?.data ?? [];
  const meta = itemsQuery.data?.meta;
  const total = meta?.total ?? 0;
  const pageStart = total === 0 ? 0 : (meta?.offset ?? 0) + 1;
  const pageEnd = Math.min((meta?.offset ?? 0) + (meta?.limit ?? 0), total);
  const canGoBack = (meta?.offset ?? 0) > 0;
  const canGoForward = pageEnd < total;

  const selectedItems = items.filter((item) => selected.includes(item.id));
  const suggestions = suggestionsQuery.data ?? { license: [], series: [], exclusiveTo: [], location: [] };
  const allOnPageSelected = items.length > 0 && selectedItems.length === items.length;
  // Look the item up by id so the card reflects refetched data.
  const detailItem = detailId ? (items.find((item) => item.id === detailId) ?? null) : null;
  const activeChips: Array<{ key: keyof ItemFilters; label: string }> = [];
  if (filters.q) activeChips.push({ key: "q", label: `Search: ${filters.q}` });
  if (filters.categoryId) activeChips.push({ key: "categoryId", label: `Category: ${categoryOptions.find((option) => option.id === filters.categoryId)?.label ?? "Selected"}` });
  if (filters.status !== DEFAULT_FILTERS.status) activeChips.push({ key: "status", label: `Status: ${filters.status}` });
  if (filters.condition) activeChips.push({ key: "condition", label: `Condition: ${filters.condition}` });
  if (filters.series) activeChips.push({ key: "series", label: `Series: ${filters.series}` });
  if (filters.location) activeChips.push({ key: "location", label: `Location: ${filters.location}` });
  if (filters.forSale) activeChips.push({ key: "forSale", label: "For sale" });
  if (filters.featured) activeChips.push({ key: "featured", label: "Featured" });
  if (filters.needsValuation) activeChips.push({ key: "needsValuation", label: "Needs valuation" });
  if (filters.sort !== DEFAULT_FILTERS.sort) activeChips.push({ key: "sort", label: `Sort: ${filters.sort}` });

  function toggleOne(id: string) {
    setSelected((previous) =>
      previous.includes(id) ? previous.filter((entry) => entry !== id) : [...previous, id],
    );
  }

  function toggleAllOnPage() {
    setSelected(allOnPageSelected ? [] : items.map((item) => item.id));
  }

  /** Fill empty license/series/exclusivity/box # on unlinked POPs. */
  async function runAutofill() {
    setBulkError(null);
    setAutofill((current) => ({ ...current, busy: true }));
    try {
      const response = await api.post<{ data: GtPoppediaAutofillSummary }>("/gt-poppedia/autofill", { limit: 25 });
      setAutofill({ busy: false, summary: response.data });
      await itemsQuery.refetch();
    } catch (error) {
      setAutofill({ busy: false, summary: null });
      setBulkError(error instanceof Error ? error.message : "GT Poppedia fill failed.");
    }
  }

  async function exportSelection() {
    setBulkError(null);
    try {
      await downloadCsv(
        `/transfer/export/backup?ids=${encodeURIComponent(selected.join(","))}`,
        "farnsworth-selection.csv",
      );
    } catch (error) {
      setBulkError(error instanceof Error ? error.message : "Export failed.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Inventory</h1>
          <p className="text-sm text-slate-400">
            {total} item{total === 1 ? "" : "s"}
            {total > 0 ? ` · showing ${pageStart}–${pageEnd}` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setTransferTab("import")}>
            Import
          </Button>
          <Button variant="secondary" onClick={() => setTransferTab("export")}>
            Export
          </Button>
          <Button variant="secondary" disabled={autofill.busy} onClick={() => void runAutofill()}>
            {autofill.busy ? "Filling…" : "Fill gaps from GT Poppedia"}
          </Button>
          <Button onClick={() => setEditorTarget(null)}>New item</Button>
        </div>
      </div>

      {autofill.summary ? (
        <Card className="space-y-1">
          <p className="text-sm text-slate-200">
            GT Poppedia: {autofill.summary.filled} of {autofill.summary.scanned} item(s) filled
            {autofill.summary.linked ? ` · ${autofill.summary.linked} already linked` : ""}
            {autofill.summary.matched ? ` · ${autofill.summary.matched} matched by name` : ""}
            {autofill.summary.unmatched ? ` · ${autofill.summary.unmatched} with no record` : ""}
            {autofill.summary.ambiguous ? ` · ${autofill.summary.ambiguous} with several records (skipped)` : ""}
            {autofill.summary.errors ? ` · ${autofill.summary.errors} error(s)` : ""}
          </p>
          <p className="text-xs text-slate-400">
            Only empty fields are filled. A field you already set is never blanked or overwritten, and an item
            with more than one possible GT Poppedia record is left alone for you to choose.
          </p>
          {autofill.summary.ambiguous > 0 || autofill.summary.errors > 0 ? (
            <ul className="max-h-32 space-y-0.5 overflow-y-auto text-xs text-amber-300">
              {autofill.summary.results
                .filter((row) => row.status === "ambiguous" || row.status === "error")
                .slice(0, 20)
                .map((row) => (
                  <li key={row.id}>
                    {row.name}: {row.message ?? (row.status === "ambiguous" ? "Several records match." : "Failed.")}
                  </li>
                ))}
            </ul>
          ) : null}
        </Card>
      ) : null}

      {activeChips.length ? <div className="flex flex-wrap items-center gap-2" aria-label="Active filters">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Active filters</span>
        {activeChips.map((chip) => <button key={chip.key} type="button" onClick={() => removeFilter(chip.key)} className="inline-flex min-h-8 items-center gap-2 rounded-full border border-indigo-800 bg-indigo-950/60 px-3 text-xs text-indigo-200 hover:bg-indigo-900" aria-label={`Remove ${chip.label}`}>
          {chip.label}<span aria-hidden="true">×</span>
        </button>)}
        <Button type="button" variant="ghost" className="min-h-8 px-2 py-1 text-xs" onClick={resetFilters}>Clear all</Button>
      </div> : null}

      <Card className="space-y-3">
        <div className="grid gap-3 md:grid-cols-4">
          <Field label="Search" className="md:col-span-2">
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Name, series, box, barcode, location…"
            />
          </Field>
          <Field label="Category">
            <Select
              value={filters.categoryId}
              onChange={(event) => patchFilters({ categoryId: event.target.value })}
            >
              <option value="">All categories</option>
              {categoryOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status">
            <Select
              value={filters.status}
              onChange={(event) =>
                patchFilters({ status: event.target.value as ItemFilters["status"] })
              }
            >
              <option value="available">Available</option>
              <option value="sold">Sold</option>
              <option value="all">All active</option>
              <option value="deleted">Recycle bin</option>
            </Select>
          </Field>
          <Field label="Condition">
            <Select
              value={filters.condition}
              onChange={(event) => patchFilters({ condition: event.target.value })}
            >
              <option value="">Any condition</option>
              {ITEM_CONDITIONS.map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Series">
            <Input value={filters.series} onChange={(event) => patchFilters({ series: event.target.value })} placeholder="e.g. Pop! Heroes" />
          </Field>
          <Field label="Location">
            <Input value={filters.location} onChange={(event) => patchFilters({ location: event.target.value })} placeholder="e.g. Bin A1" />
          </Field>
          <Field label="Sort by">
            <Select
              value={filters.sort}
              onChange={(event) => patchFilters({ sort: event.target.value as ItemFilters["sort"] })}
            >
              <option value="updatedAt">Recently updated</option>
              <option value="createdAt">Recently added</option>
              <option value="name">Name</option>
              <option value="series">Series</option>
              <option value="currentValue">Current value</option>
              <option value="forSalePrice">Asking price</option>
            </Select>
          </Field>
          <Field label="Direction">
            <Select
              value={filters.order}
              onChange={(event) =>
                patchFilters({ order: event.target.value as ItemFilters["order"] })
              }
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </Select>
          </Field>
          <Field label="Per page">
            <Select
              value={String(filters.limit)}
              onChange={(event) => patchFilters({ limit: Number(event.target.value) })}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </Field>
          {filtersActive ? <div className="flex items-end"><Button variant="ghost" onClick={resetFilters}>Reset filters</Button></div> : null}
        </div>
      </Card>

      <Section title="Quick views" defaultOpen={false}>
        <div>
          <div className="flex flex-wrap gap-2">
            {["Available", "For sale", "Featured", "Sold", "Needs valuation", "Recently added"].map((name) => (
              <Button key={name} type="button" variant="secondary" onClick={() => applyQuickView(name)}>{name}</Button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 border-t border-slate-800 pt-3 sm:flex-row sm:items-end">
          <Field label="Saved views" className="min-w-0 flex-1">
            <Select value={selectedSavedView} onChange={(event) => selectSavedView(event.target.value)}>
              <option value="">Choose a saved view</option>
              {savedViews.map((saved) => <option key={saved.id} value={saved.id}>{saved.name}</option>)}
            </Select>
          </Field>
          <Field label="Save current filters as" className="min-w-0 flex-1">
            <Input value={savedViewName} onChange={(event) => setSavedViewName(event.target.value)} placeholder="e.g. Marvel shelf" maxLength={60} />
          </Field>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" disabled={!savedViewName.trim()} onClick={saveCurrentView}>Save view</Button>
            {selectedSavedView ? <Button type="button" variant="ghost" onClick={() => {
              const next = savedViews.filter((saved) => saved.id !== selectedSavedView);
              setSavedViews(next);
              window.localStorage.setItem("inventory-saved-views", JSON.stringify(next));
              setSelectedSavedView("");
            }}>Delete view</Button> : null}
          </div>
        </div>
      </Section>

      {itemsQuery.isError ? <ErrorBanner message={(itemsQuery.error as Error).message} /> : null}
      {bulkError ? <ErrorBanner message={bulkError} /> : null}

      {selected.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-800 bg-indigo-950/40 px-3 py-2">
          <span className="text-sm font-medium text-indigo-100">{selected.length} selected</span>
          <div className="flex flex-wrap gap-2">
{recycleBin ? (
              <Button variant="secondary" onClick={() => bulkRestore.mutate(selected, { onSuccess: () => setSelected([]) })}>
                {bulkRestore.isPending ? "Restoring…" : "Restore selected"}
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setShowBundle(true)}>
                Bundle
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowMassEdit(true)}>
              Mass edit
            </Button>
            <Button variant="secondary" onClick={exportSelection}>
              Export selected
            </Button>
            <Button variant="danger" onClick={() => setBulkDeleteOpen(true)}>
              Delete
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setSelected([])}
            className="ml-auto text-sm text-indigo-200 hover:text-white"
          >
            Clear selection
          </button>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3"><span className="text-xs text-slate-400">{recycleBin ? "Deleted inventory" : filtersActive ? "Filtered inventory" : "Available inventory"}</span><div className="inline-flex rounded-lg border border-slate-800 bg-slate-950 p-1"><Button className="rounded-r-none" variant={view==="table"?"secondary":"ghost"} aria-pressed={view==="table"} onClick={()=>changeView("table")}>Table</Button><Button className="rounded-l-none" variant={view==="cards"?"secondary":"ghost"} aria-pressed={view==="cards"} onClick={()=>changeView("cards")}>Cards</Button></div></div>
      <Card className="p-0">
        {itemsQuery.isLoading ? (
          <Spinner label="Loading inventory…" />
        ) : items.length === 0 ? (
          <EmptyState
            title="No items match these filters"
            hint="Adjust the filters above, or add a new item."
          />
        ) : view === "cards" ? (
          <div className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3">{items.map(item=><button type="button" key={item.id} onClick={()=>setDetailId(item.id)} className="rounded-lg border border-slate-800 p-3 text-left hover:bg-slate-800/40"><div className="flex gap-3">{item.imageUrls[0]?<img src={`/api/items/${item.id}/images/0`} alt="" className="h-16 w-16 rounded object-cover"/>:<div className="h-16 w-16 rounded bg-slate-800"/>}<div className="min-w-0"><div className="truncate font-medium text-slate-100">{item.name}</div><div className="text-xs text-slate-400">{[item.license,item.series].filter(Boolean).join(" · ")||"—"}</div><div className="mt-2 text-sm text-slate-200">{formatCurrency(item.currentValue)}</div><StatusBadges item={item}/></div></div></button>)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                      className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-indigo-600"
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Item</th>
                  <th className="px-3 py-2 font-medium">License</th>
                  <th className="px-3 py-2 font-medium">Box</th>
                  <th className="px-3 py-2 font-medium">Location</th>
                  <th className="px-3 py-2 font-medium">Owner</th>
                  <th className="px-3 py-2 text-right font-medium">Qty</th>
                  <th className="px-3 py-2 text-right font-medium">Value</th>
                  <th className="px-3 py-2 text-right font-medium">Asking</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Updated</th>
                  <th scope="col" className="px-3 py-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {items.map((item) => {
                  const isSelected = selected.includes(item.id);
                  return (
                    <tr
                      key={item.id}
                      onClick={() => setDetailId(item.id)}
                      className={cn(
                        "cursor-pointer hover:bg-slate-800/40",
                        isSelected && "bg-indigo-950/30",
                      )}
                    >
                      <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${item.name}`}
                          checked={isSelected}
                          onChange={() => toggleOne(item.id)}
                          className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-indigo-600"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {item.imageUrls[0] ? (
                            <img
                              src={`/api/items/${item.id}/images/0`}
                              alt=""
                              className="h-9 w-9 rounded object-cover"
                              loading="lazy"
                              onError={(event) => {
                                (event.target as HTMLImageElement).style.visibility = "hidden";
                              }}
                            />
                          ) : (
                            <div className="h-9 w-9 rounded bg-slate-800" />
                          )}
                          <div>
                            <div className="font-medium text-slate-100">{item.name}</div>
                            <div className="text-xs text-slate-400">
                              {[item.series, item.exclusiveTo].filter(Boolean).join(" · ") || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-300">{item.license ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-300">{item.boxNo ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-300">{item.location ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-300">{item.owner ?? "—"}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{item.quantity}</td>
                      <td className="px-3 py-2 text-right text-slate-200">
                        {formatCurrency(item.currentValue)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-200">
                        {formatCurrency(item.forSalePrice)}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadges item={item} />
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-400">
                        {formatDate(item.updatedAt)}
                      </td>
                      <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" onClick={() => setDetailId(item.id)}>
                            View
                          </Button>
{item.deletedAt ? (
                            <Button variant="ghost" onClick={() => restoreItem.mutate(item.id)}>
                              Restore
                            </Button>
                          ) : (
                            <>
                              <Button variant="ghost" onClick={() => setEditorTarget(item)}>
                                Edit
                              </Button>
                              <Button variant="ghost" onClick={() => setDeleteTarget(item)}>
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > 0 ? (
        <div className="flex items-center justify-between text-sm text-slate-400">
          <span>Page {Math.floor((meta?.offset ?? 0) / (meta?.limit ?? 25)) + 1}</span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={!canGoBack}
              onClick={() =>
                patchFilters({ offset: Math.max(0, (meta?.offset ?? 0) - (meta?.limit ?? 25)) })
              }
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={!canGoForward}
              onClick={() => patchFilters({ offset: (meta?.offset ?? 0) + (meta?.limit ?? 25) })}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      {transferTab ? (
        <TransferDialog initialTab={transferTab} onClose={() => setTransferTab(null)} />
      ) : null}

      {showMassEdit ? (
        <MassEditDialog
          ids={selected}
          categories={categoriesQuery.data ?? []}
          onClose={() => {
            setShowMassEdit(false);
            setSelected([]);
          }}
        />
      ) : null}

      {showBundle ? (
        <BundleDialog items={selectedItems} onClose={() => setShowBundle(false)} />
      ) : null}

      {detailItem ? (
        <ItemDetailDialog
          item={detailItem}
          categories={categoriesQuery.data ?? []}
          onClose={() => setDetailId(null)}
          onEdit={() => {
            const target = detailItem;
            setDetailId(null);
            setEditorTarget(target);
          }}
        />
      ) : null}

      {editorTarget !== undefined ? (
        <ItemEditor key={editorTarget?.id ?? "new"}
          item={editorTarget}
          categories={categoriesQuery.data ?? []}
          suggestions={suggestions}
          onClose={() => setEditorTarget(undefined)}
        />
      ) : null}

      {deleteTarget ? (
        <Modal title={`Delete "${deleteTarget.name}"?`} onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-slate-300">
            This soft-deletes the item. It is hidden from the inventory but the record and its audit
            history are retained.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={deleteItem.isPending}
              onClick={async () => {
                await deleteItem.mutateAsync(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              {deleteItem.isPending ? "Deleting…" : "Delete item"}
            </Button>
          </div>
        </Modal>
      ) : null}

      {bulkDeleteOpen ? (
        <Modal
          title={`Delete ${selected.length} item(s)?`}
          onClose={() => setBulkDeleteOpen(false)}
        >
          <p className="text-sm text-slate-300">
            This soft-deletes the selected items. They are hidden from the inventory but their
            records and audit history are retained.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setBulkDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={bulkDelete.isPending}
              onClick={async () => {
                await bulkDelete.mutateAsync(selected);
                setBulkDeleteOpen(false);
                setSelected([]);
              }}
            >
              {bulkDelete.isPending ? "Deleting…" : "Delete items"}
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
